<template>
  <div class="flex items-center otp-cell__container flex flex-col">
    <input
        :class="[inputClass, 'otp-cell__input', { error }]"
        :type="inputTypeValue"
        ref="input"
        :maxlength="1"
        pattern="[0-9]"
        :value="computedModel"
        placeholder="0"
        @paste="onPaste"
        @input="onInput"
        @focus="onFocus"
        @click="onFocus"
        @keydown="onKeydown"
    />
  </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue'

const KEY_CODE = {
  BACKSPACE: 'Backspace',
  RIGHT: 'ArrowRight',
  LEFT: 'ArrowLeft',
  UP: 'ArrowUp',
  DOWN: 'ArrowDown',
}

const props = defineProps({
  inputType: {
    type: String,
    validator: (type) => ['number', 'tel', 'password'].includes(type),
    default: 'number',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
  active: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:modelValue', 'next', 'prev', 'focus', 'paste'])

const input = ref(null)

const inputTypeValue = computed(() => props.inputType)

const computedModel = computed(() => props.modelValue?.slice(-1))

const onFocus = () => {
  emit('focus')
}

const onPaste = (value) => {
  emit('paste', value)
}

const onInput = (value) => {
  emit('update:modelValue', value.data)
  if (value.data) {
    emit('next')
  }
}

const onKeydown = (evt) => {
  if (evt.key === KEY_CODE.BACKSPACE && !computedModel.value) {
    emit('prev')
  } else if (evt.key === KEY_CODE.RIGHT) {
    emit('next')
    evt.preventDefault()
  } else if (evt.key === KEY_CODE.LEFT) {
    emit('prev')
    evt.preventDefault()
  } else if (evt.key === KEY_CODE.DOWN || evt.key === KEY_CODE.UP) {
    evt.preventDefault()
  }
}

watch(() => props.active,
    (newValue) => {
      if (input.value && newValue) {
        input.value.focus()
        input.value.select()
      }
    }
)
</script>

<style scoped lang="scss">
.otp-cell {
  &__input {
    @apply transition-colors duration-300 w-[70px] aspect-square text-center;
    @apply border-solid border-[var(--bg-3)] border-[1px] bg-transparent;
    @apply outline-none text-[28px] font-[700] text-[var(--text-1)] rounded-[12px];
    @apply focus:border-[var(--brand)] placeholder-[var(--bg-3)];
    @apply bg-[var(--bg-1)];
    @media (max-width: 768px) {
      width: 50px;
      font-size: 24px;
    }
    @media (max-width: 425px) {
      width: 46px;
      font-size: 20px;
    }
    @media (max-width: 375px) {
      width: 42px;
      font-size: 18px;
    }
    &.error {
      @apply text-[var(--error)];
    }
  }
}

input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
